import { CartUpdateEvent, ThemeEvents } from "@theme/events";

/**
 * cart-pickup-fee.js — strip the Taiwan pickup-fee line whenever the cart renders OUTSIDE checkout.
 *
 * Checkout charges the NT$80 store-pickup fee as a taxable `pickup-fee` product line (Shopify
 * never applies VAT to a pickup option's cost on the tax-inclusive TW store). The checkout
 * extension that adds/removes that line only runs inside checkout, so a buyer who backs out to
 * the cart page, opens the drawer, or abandons the checkout would otherwise see (and could edit)
 * a "門市取貨手續費" row. This module removes every line carrying the `_pickup_fee` property on
 * page load, on bfcache restore and after any cart update, and re-renders the cart sections.
 * When the buyer returns to checkout with pickup still selected, the extension adds the line
 * back. Correctness never depends on this file — the pickup-fee-guard validation blocks any
 * inconsistent cart at Pay; this is purely so the fee is only ever visible at checkout.
 *
 * Contract: the line-item property is `_pickup_fee` (set by the checkout-custom-ux
 * `pickup-fee-line` extension). Design: shopify-cloudflare-functions/ma-carrier-service/docs/TW-PICKUP-FEE-VAT.md
 */
const FEE_PROPERTY = "_pickup_fee";
const SOURCE = "cart-pickup-fee";

let inFlight = null;

function collectCartSectionIds() {
  const ids = new Set();
  for (const el of document.querySelectorAll("cart-items-component")) {
    if (el instanceof HTMLElement && el.dataset.sectionId) ids.add(el.dataset.sectionId);
  }
  return Array.from(ids);
}

async function fetchCart() {
  const res = await fetch("/cart.js", { headers: { Accept: "application/json" } });
  if (!res.ok) throw new Error(`cart.js ${res.status}`);
  return res.json();
}

function feeItems(cart) {
  return (cart.items || []).filter((item) => item.properties && item.properties[FEE_PROPERTY] != null);
}

async function removeFeeLines() {
  const cart = await fetchCart();
  let targets = feeItems(cart);
  if (targets.length === 0) return;

  // Line keys change after every mutation, so remove one line per request and re-read the cart
  // from the response before picking the next one. Ask for the rendered sections on the last call
  // so the cart page / drawer rows refresh in the same round trip (same shape as cart-attribute-sync).
  let updated = null;
  while (targets.length > 0) {
    const item = targets[0];
    const body = { id: item.key, quantity: 0 };
    if (targets.length === 1) {
      const sectionIds = collectCartSectionIds();
      if (sectionIds.length > 0) {
        body.sections = sectionIds.join(",");
        body.sections_url = window.location.pathname;
      }
    }
    const res = await fetch("/cart/change.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify(body),
    });
    updated = await res.json().catch(() => null);
    if (!updated || updated.status) return; // {status, message} = Shopify error; leave it
    targets = feeItems(updated);
  }

  // Same auto-open dance as cart-attribute-sync.js: a cart:update would otherwise pop the drawer.
  const autoOpenDrawers = Array.from(document.querySelectorAll("cart-drawer-component[auto-open]"));
  for (const drawer of autoOpenDrawers) drawer.removeAttribute("auto-open");

  document.dispatchEvent(
    new CartUpdateEvent(updated, SOURCE, {
      itemCount: updated.item_count,
      source: SOURCE,
      sections: updated.sections ?? undefined,
    })
  );

  setTimeout(() => {
    for (const drawer of autoOpenDrawers) drawer.setAttribute("auto-open", "");
  }, 0);
}

function run() {
  if (inFlight) return inFlight;
  inFlight = removeFeeLines()
    .catch(() => {})
    .finally(() => {
      inFlight = null;
    });
  return inFlight;
}

// Page load (covers "back to cart" from checkout and the drawer on any page).
run();
// bfcache restore: the page comes back without re-executing modules.
window.addEventListener("pageshow", (event) => {
  if (event.persisted) run();
});
// Any other cart mutation (our own dispatch included — it re-checks, finds nothing, stops).
document.addEventListener(ThemeEvents.cartUpdate, (event) => {
  if (event?.detail?.data?.source === SOURCE) return;
  run();
});
